/**
 * Entries module DTOs (§4.2 B/C, §15.2): `POST /v1/l/:slug/entries`, `GET /v1/entries/:id/receipt`.
 * Consent is separable (§12.5): participation, sponsor communication and marketing are independent, unticked.
 */
import { z } from 'zod';
import { CountryCode, E164Phone, IsoDateTime, LocaleTag, PositiveInt, SocialHandle, Ulid, paginated, } from './common.js';
import { EngagementTaskType, EntrySource, EntryStatus, LotteryMechanic, PaymentStatus, SocialPlatform, TaskCompletionStatus, } from './enums.js';
import { ReferralCode } from './ids.js';
import { ConfigVersion } from './lottery-config.js';
import { MoneyDto } from './money.js';
export const ContactInput = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
    z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
]);
/** §12.5 consents: participation is required; the others are independent and default to false (unticked). */
export const ConsentInput = z.object({
    participation: z.literal(true),
    sponsorCommunication: z.boolean().default(false),
    marketing: z.boolean().default(false),
    /** Optional, separately consented winner publicity (§2.4). */
    winnerPublicity: z.boolean().default(false),
    /** Version of the consent text shown. */
    consentTextVersion: z.string().min(1).max(32),
});
/**
 * Quick Pick ticket (E2E-06, D-2304): `POST /l/:slug/quick-pick` signs every set it returns (HMAC over the
 * lottery, its published version and the sorted numbers, base64url, 43 chars). Sending the numbers back with
 * their ticket makes the receipt carry exactly the set the participant saw before paying (§21.1 #6).
 */
export const QuickPickTicket = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'invalid quick pick ticket');
/** §9.3: manual selections or Quick Pick, one selection per entry unit. */
export const NumberPickSelection = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('MANUAL'), numbers: z.array(z.number().int()).min(1).max(20) }),
    z.object({
        kind: z.literal('QUICK_PICK'),
        /**
         * The exact set `POST /l/:slug/quick-pick` returned, with its `ticket` (both or neither). Omitted: the server
         * draws a fresh set at entry time. Present: the server verifies the ticket and stores these numbers with
         * `quickPick = true`.
         */
        numbers: z.array(z.number().int()).min(1).max(20).optional(),
        ticket: QuickPickTicket.optional(),
    }),
]);
/** §4.1 Eligibility: the participant confirms age / location with minimal data; the server applies the checks. */
export const EligibilityAttestations = z.object({
    /** "I am at least {minAge}" — unticked by default; the server answers `ineligible` with a reason when false. */
    ageConfirmed: z.boolean().default(false),
    /** ISO 3166-1 alpha-2 the participant declares; required when the published geography lists countries. */
    countryCode: CountryCode.optional(),
    residencyConfirmed: z.boolean().default(false),
});
export const CreateEntryRequest = z.object({
    displayName: z.string().trim().min(1).max(60),
    contact: ContactInput,
    attestations: EligibilityAttestations.default({ ageConfirmed: false, residencyConfirmed: false }),
    /** Platform the participant arrived from, prefilled from the link when known (§12.1). */
    sourcePlatform: SocialPlatform.optional(),
    quantity: PositiveInt.default(1),
    referralCode: ReferralCode.optional(),
    campaign: z.record(z.string().max(40), z.string().max(120)).optional(),
    consents: ConsentInput,
    /** The exact configuration, terms and privacy versions the participant saw (§5.4). */
    configVersion: ConfigVersion,
    termsVersion: z.string().min(1).max(32),
    privacyVersion: z.string().min(1).max(32),
    locale: LocaleTag.optional(),
    /** Number pick only: one selection per entry unit. */
    selections: z.array(NumberPickSelection).optional(),
    /** Paid entry: where the provider should send the participant back. */
    returnUrl: z.string().max(512).optional(),
});
export const EntryDto = z.object({
    entryId: Ulid,
    lotteryId: Ulid,
    status: EntryStatus,
    source: EntrySource,
    quantity: z.number().int(),
    configVersion: ConfigVersion,
    createdAt: IsoDateTime,
    confirmedAt: IsoDateTime.nullable(),
    /** First-N / sampling position — assigned once at first qualified entry (§8.1, §9.3). */
    participantSequence: z.number().int().nullable(),
    entrySequence: z.number().int().nullable(),
    /** Number pick: immutable selections stored on the receipt. */
    selections: z.array(z.array(z.number().int())).optional(),
    /** Number pick: whether the selections came from the server Quick Pick (§4.3). */
    quickPick: z.boolean().optional(),
    statusReason: z.string().optional(),
});
export const CreateEntryResponse = z.object({
    entry: EntryDto,
    /** Present for PAID lotteries: the participant continues to checkout; no entry is confirmed yet (§4.2 B). */
    payment: z
        .object({
        paymentId: Ulid,
        status: PaymentStatus,
        amount: MoneyDto,
        checkoutUrl: z.string().optional(),
        expiresAt: IsoDateTime.optional(),
    })
        .optional(),
    /** Instant mechanics: the server-decided outcome is fetched separately via the receipt / reveal endpoint. */
    receiptUrl: z.string(),
});
/** `GET /v1/entries/:id/receipt` — proof before promotion (§4.2 C); no contact details. */
export const EntryReceiptDto = z.object({
    entry: EntryDto,
    lottery: z.object({
        lotteryId: Ulid,
        slug: z.string(),
        title: z.string(),
        mechanic: LotteryMechanic,
        drawAt: IsoDateTime.optional(),
        closesAt: IsoDateTime,
        timezone: z.string(),
    }),
    /** Real confirmed entry count for this participant (bonus entries included). */
    confirmedEntryCount: z.number().int(),
    bonusEntries: z.number().int(),
    bonusEntryCap: z.number().int(),
    payment: z
        .object({ paymentId: Ulid, status: PaymentStatus, amount: MoneyDto, statusCopy: z.string() })
        .optional(),
    whatHappensNext: z.array(z.string()),
    /** Status copy key + rendered text (participant-safe language, Appendix A). */
    statusCopy: z.object({ key: z.string(), text: z.string(), nextAction: z.string() }).optional(),
    shareCardUrl: z.string().optional(),
    referralCode: ReferralCode.optional(),
    /** Instant mechanics only: whether an outcome exists for this entry (fetched via the outcome endpoints). */
    outcomeAvailable: z.boolean(),
    sourcePlatform: SocialPlatform.optional(),
    /** Engagement tasks with REAL progress — present only once the entry is confirmed (§4.2 C "then, and only then"). */
    tasks: z.array(z.lazy(() => ReceiptTaskDto)).default([]),
    /** Platforms the organizer configured (D06) for "link another channel". */
    platforms: z
        .array(z.object({ platform: SocialPlatform, label: z.string(), accountUrl: z.string() }))
        .default([]),
    /** Channels already on the participant profile (masked). */
    linkedPlatforms: z.array(SocialPlatform).default([]),
    /** Exact config / terms / privacy versions recorded on the entry (§4.1 Receipt "config version"). */
    versions: z
        .object({ configVersion: ConfigVersion, termsVersion: z.string(), privacyVersion: z.string() })
        .optional(),
    /** Presenting sponsor credit line (§4.1 Receipt "sponsor credit"), labelled. */
    sponsorCredit: z.object({ name: z.string(), logoUrl: z.string().optional() }).optional(),
});
export const EntryListQuery = z.object({
    lotteryId: Ulid,
    status: EntryStatus.optional(),
    source: EntrySource.optional(),
    participantId: Ulid.optional(),
});
export const EntryAdminRowDto = EntryDto.extend({
    participantId: Ulid,
    displayName: z.string(),
    contactMasked: z.string(),
    sourcePlatform: SocialPlatform.optional(),
    paymentId: Ulid.nullable(),
});
export const DisqualifyEntryRequest = z.object({
    reason: z.string().min(3).max(2000),
    evidenceRef: z.string().max(512).optional(),
});
// ---------------------------------------------------------------------------------------------------------
// Extensions (entries agent, D-1301…): receipt tasks, status, quick pick, admin rows, consent withdrawal
// ---------------------------------------------------------------------------------------------------------
/** One engagement task on the receipt with real counts only (§12.4). */
export const ReceiptTaskDto = z.object({
    taskId: Ulid,
    type: EngagementTaskType,
    title: z.string(),
    consentText: z.string(),
    bonusEntries: z.number().int(),
    platform: SocialPlatform.optional(),
    destinationUrl: z.string().optional(),
    /** Completions of this task by this participant. */
    completed: z.number().int(),
    pending: z.number().int(),
    /** Per-task cap when published (once per platform per lottery, …). */
    cap: z.number().int().optional(),
    /** Bonus entries this task has earned the participant so far. */
    bonusEarned: z.number().int(),
    /** Referral / share: the participant's tracked link (`/r/:code`). */
    shareUrl: z.string().optional(),
    referralCode: ReferralCode.optional(),
    /** Whether the participant may complete it again (cap, bonus cap, platform already linked …). */
    available: z.boolean(),
    unavailableReason: z.string().optional(),
});
/** `GET /v1/entries/:id/status` — the lightweight "check status" read (§4.4), no contact details. */
export const EntryStatusDto = z.object({
    entryId: Ulid,
    status: EntryStatus,
    source: EntrySource,
    quantity: z.number().int(),
    confirmedAt: IsoDateTime.nullable(),
    participantSequence: z.number().int().nullable(),
    payment: z.object({ paymentId: Ulid, status: PaymentStatus }).optional(),
    /** Participant-safe status copy (Appendix A) when the entry is not simply confirmed. */
    statusCopy: z.object({ key: z.string(), text: z.string(), nextAction: z.string() }).optional(),
    receiptUrl: z.string(),
});
/** `POST /v1/l/:slug/quick-pick` — server-generated picks (§4.3, §1.2 server truth). */
export const QuickPickRequest = z.object({ count: z.number().int().min(1).max(20).default(1) });
export const QuickPickResponse = z.object({
    picks: z.array(z.array(z.number().int())),
    range: z.object({ min: z.number().int(), max: z.number().int() }),
    pickCount: z.number().int(),
    /** One ticket per set in `picks` (same index), to send back as `{ kind: 'QUICK_PICK', numbers, ticket }`. */
    tickets: z.array(QuickPickTicket).default([]),
});
/** Admin list (`GET /v1/admin/lotteries/:id/entries`), masked by default (§16.3). */
export const EntryAdminListQuery = z.object({
    status: EntryStatus.optional(),
    source: EntrySource.optional(),
    participantId: Ulid.optional(),
    sourcePlatform: SocialPlatform.optional(),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const EntryAdminRowFullDto = EntryAdminRowDto.extend({
    /** Signals recorded on the participant profile (§17.4): reasons for review, never standalone guilt. */
    riskFlags: z.array(z.object({ code: z.string(), at: IsoDateTime, reviewed: z.boolean() })).default([]),
    paymentStatus: PaymentStatus.nullable(),
    statusReason: z.string().nullable(),
    disqualifiedBy: z.string().nullable(),
    disqualifiedAt: IsoDateTime.nullable(),
});
export const EntryAdminPage = paginated(EntryAdminRowFullDto);
/** `GET /v1/admin/lotteries/:id/entries/summary` — counts the console shows above the list. */
export const EntryCountsDto = z.object({
    lotteryId: Ulid,
    byStatus: z.record(EntryStatus, z.number().int()),
    bySource: z.record(EntrySource, z.number().int()),
    uniqueParticipants: z.number().int(),
    riskFlagged: z.number().int(),
});
/** `POST /v1/participants/me/consent/withdraw` response (Appendix A "Participant withdraws marketing consent"). */
export const ConsentWithdrawalDto = z.object({
    purpose: z.enum(['SPONSOR_COMMUNICATION', 'MARKETING', 'WINNER_PUBLICITY']),
    withdrawnAt: IsoDateTime,
    /** True when a matching, non-withdrawn consent existed; false means nothing was on file (still recorded). */
    hadConsent: z.boolean(),
});
/** Admin review queue row for engagement completions (§12.4 anti-abuse, §17.4 reason codes). */
export const TaskCompletionAdminRowDto = z.object({
    completionId: Ulid,
    lotteryId: Ulid,
    taskId: Ulid,
    type: EngagementTaskType,
    status: TaskCompletionStatus,
    participantId: Ulid,
    displayName: z.string(),
    contactMasked: z.string(),
    platform: SocialPlatform.optional(),
    bonusAwarded: z.number().int(),
    /** Reason codes that put the completion in the queue (`velocity_ip`, `spot_check`, …). */
    reasonCodes: z.array(z.string()),
    evidence: z.record(z.string(), z.unknown()),
    reviewedBy: z.string().nullable(),
    reviewReason: z.string().nullable(),
    createdAt: IsoDateTime,
    qualifiedAt: IsoDateTime.nullable(),
    rejectedAt: IsoDateTime.nullable(),
});
export const TaskCompletionListQuery = z.object({
    lotteryId: Ulid.optional(),
    status: TaskCompletionStatus.optional(),
    type: EngagementTaskType.optional(),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const TaskCompletionAdminPage = paginated(TaskCompletionAdminRowDto);
/** §4.2 C "What happens next" lines per mechanic family (participant-facing copy lives in contracts). */
export const WHAT_HAPPENS_NEXT = {
    SCHEDULED: [
        'Keep your entry ID — it is your proof of entry and connects your receipt to the published draw record.',
        'The draw runs at the published time in the lottery timezone; results and the fairness bundle are published right after.',
        'Winners receive a claim link on the contact they entered with. Nobody will ask you to pay to claim.',
    ],
    INSTANT: [
        'Your outcome was decided on our side the moment your entry was confirmed; the reveal only presents it.',
        'Open your reveal whenever you like — skipping the animation never changes the result.',
        'Winners continue straight to the claim steps with their win ID and claim deadline.',
    ],
    FIRST_N: [
        'Your qualified position is assigned once, at your first confirmed entry, and never shifts.',
        'The published milestone rule decides the outcome; it is shown next to your position.',
        'Winners continue straight to the claim steps with their win ID and claim deadline.',
    ],
    PENDING_PAYMENT: [
        'Your entry is not in the draw until the payment is confirmed by the provider.',
        'Use "Check status" rather than paying again — the same checkout is reused.',
        'Once confirmed, this page shows your entry ID and confirmed count.',
    ],
};
//# sourceMappingURL=entries.js.map